/**
 * The unlock economy (FS-10 §2.2, §3.2, §4.3). Every unlock is an achievement gate evaluated over
 * persisted `RunSummary`s (R13) — never live sim state — so it cannot be triggered by a mid-run bug
 * and cannot be lost to a crash before the run ends. Granting is idempotent and never revocable
 * (R17): `evaluate()` returns everything currently satisfied; the caller unions it into
 * `profile.unlocked`.
 *
 * `run-stat-all` and `hit-free-sector` extend the FS-10 §3.2 `UnlockRule` union by two variants.
 * The spec's five variants cover every unlock except `Null`'s compound "sector 5 complete AND an 8×
 * multiplier in one run" — which is one run meeting two conditions at once, not two separately
 * satisfiable ones — and `Filament`'s "any sector without being hit", which is a run-level boolean
 * rather than a numeric stat. Both are still evaluated the same way: over `profile.recentRuns` only.
 */
import type { HullId } from '../player/hulls/types';
import type { ModeId } from '../game/modes/types';
import type { LifetimeStats, Profile, RunStatKey, RunSummary } from './profile';

export type UnlockableId =
  | { kind: 'hull'; id: HullId }
  | { kind: 'sector'; id: number }
  | { kind: 'mode'; id: ModeId };

export type LifetimeStatKey = keyof LifetimeStats;

export type UnlockRule =
  | { kind: 'always' }
  | { kind: 'sector-complete'; requiredSectorIndex: number; modes: readonly (ModeId | 'any')[] }
  | { kind: 'score'; threshold: number; modes: readonly (ModeId | 'any')[] }
  | { kind: 'run-stat'; stat: RunStatKey; threshold: number; modes: readonly (ModeId | 'any')[] }
  | { kind: 'lifetime-stat'; stat: LifetimeStatKey; threshold: number }
  | { kind: 'all-of'; ids: readonly UnlockableId[] }
  | { kind: 'hit-free-sector'; modes: readonly (ModeId | 'any')[] }
  | {
      kind: 'run-stat-all';
      conditions: readonly { stat: RunStatKey; threshold: number }[];
      modes: readonly (ModeId | 'any')[];
    };

export interface UnlockDef {
  readonly id: UnlockableId;
  readonly rule: UnlockRule;
  /** Shown before it is met (R11). */
  readonly description: string;
  readonly progressKind: 'fraction' | 'best' | 'checklist';
}

function idKey(id: UnlockableId): string {
  return `${id.kind}:${id.id}`;
}

function modeAccepts(modes: readonly (ModeId | 'any')[], run: RunSummary): boolean {
  return modes.includes('any') || modes.includes(run.modeId);
}

function runStat(run: RunSummary, stat: RunStatKey): number {
  if (stat === 'sectorIndexCleared') return run.sectorCleared;
  return run[stat];
}

export const UNLOCKS: readonly UnlockDef[] = [
  { id: { kind: 'hull', id: 'Vector' }, rule: { kind: 'always' }, description: 'Available from the start.', progressKind: 'checklist' },
  { id: { kind: 'sector', id: 1 }, rule: { kind: 'always' }, description: 'Available from the start.', progressKind: 'checklist' },
  { id: { kind: 'mode', id: 'sector-run' }, rule: { kind: 'always' }, description: 'Available from the start.', progressKind: 'checklist' },
  { id: { kind: 'mode', id: 'infinite' }, rule: { kind: 'always' }, description: 'Available from the start.', progressKind: 'checklist' },
  { id: { kind: 'mode', id: 'zen' }, rule: { kind: 'always' }, description: 'Available from the start.', progressKind: 'checklist' },
  { id: { kind: 'mode', id: 'custom' }, rule: { kind: 'always' }, description: 'Available from the start.', progressKind: 'checklist' },

  { id: { kind: 'sector', id: 2 }, rule: { kind: 'sector-complete', requiredSectorIndex: 1, modes: ['sector-run', 'infinite'] }, description: 'Complete sector 1 in Sector Run, or reach sector 2 in Infinite Frequency.', progressKind: 'checklist' },
  { id: { kind: 'sector', id: 3 }, rule: { kind: 'sector-complete', requiredSectorIndex: 2, modes: ['sector-run', 'infinite'] }, description: 'Complete sector 2 in Sector Run, or reach sector 3 in Infinite Frequency.', progressKind: 'checklist' },
  { id: { kind: 'sector', id: 4 }, rule: { kind: 'sector-complete', requiredSectorIndex: 3, modes: ['sector-run', 'infinite'] }, description: 'Complete sector 3 in Sector Run, or reach sector 4 in Infinite Frequency.', progressKind: 'checklist' },
  { id: { kind: 'sector', id: 5 }, rule: { kind: 'sector-complete', requiredSectorIndex: 4, modes: ['sector-run', 'infinite'] }, description: 'Complete sector 4 in Sector Run, or reach sector 5 in Infinite Frequency.', progressKind: 'checklist' },
  { id: { kind: 'sector', id: 6 }, rule: { kind: 'sector-complete', requiredSectorIndex: 5, modes: ['sector-run', 'infinite'] }, description: 'Complete sector 5 in Sector Run, or reach sector 6 in Infinite Frequency.', progressKind: 'checklist' },
  { id: { kind: 'sector', id: 7 }, rule: { kind: 'sector-complete', requiredSectorIndex: 6, modes: ['sector-run', 'infinite'] }, description: 'Complete sector 6 in Sector Run, or reach sector 7 in Infinite Frequency.', progressKind: 'checklist' },
  { id: { kind: 'sector', id: 8 }, rule: { kind: 'sector-complete', requiredSectorIndex: 7, modes: ['sector-run', 'infinite'] }, description: 'Complete sector 7 in Sector Run, or reach sector 8 in Infinite Frequency.', progressKind: 'checklist' },

  { id: { kind: 'mode', id: 'gauntlet' }, rule: { kind: 'lifetime-stat', stat: 'bossesDefeated', threshold: 2 }, description: 'Defeat any 2 bosses.', progressKind: 'fraction' },
  { id: { kind: 'mode', id: 'daily' }, rule: { kind: 'sector-complete', requiredSectorIndex: 1, modes: ['sector-run', 'infinite'] }, description: 'Complete sector 1.', progressKind: 'checklist' },

  { id: { kind: 'hull', id: 'Bassline' }, rule: { kind: 'run-stat', stat: 'hazardsCleared', threshold: 500, modes: ['any'] }, description: 'Clear 500 hazards in one run.', progressKind: 'best' },
  { id: { kind: 'hull', id: 'Prism' }, rule: { kind: 'lifetime-stat', stat: 'overdrives', threshold: 10 }, description: 'Reach Overdrive 10 times, lifetime.', progressKind: 'fraction' },
  { id: { kind: 'hull', id: 'Metronome' }, rule: { kind: 'run-stat', stat: 'beatSteers', threshold: 200, modes: ['any'] }, description: '200 beat-accurate steers in one run.', progressKind: 'best' },
  { id: { kind: 'hull', id: 'Filament' }, rule: { kind: 'hit-free-sector', modes: ['sector-run', 'gauntlet'] }, description: 'Complete any sector without being hit.', progressKind: 'checklist' },
  {
    id: { kind: 'hull', id: 'Null' },
    rule: {
      kind: 'run-stat-all',
      conditions: [
        { stat: 'sectorIndexCleared', threshold: 5 },
        { stat: 'multiplierPeak', threshold: 8 },
      ],
      modes: ['sector-run', 'gauntlet', 'infinite'],
    },
    description: 'Complete sector 5 and reach an 8× multiplier in one run.',
    progressKind: 'checklist',
  },
];

function ruleSatisfied(rule: UnlockRule, profile: Profile): boolean {
  switch (rule.kind) {
    case 'always':
      return true;
    case 'sector-complete':
      return profile.recentRuns.some((r) => modeAccepts(rule.modes, r) && r.sectorCleared >= rule.requiredSectorIndex);
    case 'score':
      return profile.recentRuns.some((r) => modeAccepts(rule.modes, r) && r.score >= rule.threshold);
    case 'run-stat':
      return profile.recentRuns.some((r) => modeAccepts(rule.modes, r) && runStat(r, rule.stat) >= rule.threshold);
    case 'lifetime-stat':
      return profile.lifetime[rule.stat] >= rule.threshold;
    case 'hit-free-sector':
      return profile.recentRuns.some((r) => modeAccepts(rule.modes, r) && r.won && r.hitsTaken === 0);
    case 'run-stat-all':
      return profile.recentRuns.some(
        (r) => modeAccepts(rule.modes, r) && rule.conditions.every((c) => runStat(r, c.stat) >= c.threshold),
      );
    case 'all-of':
      return rule.ids.every((id) => isUnlocked(profile, id));
  }
}

export function isUnlocked(profile: Profile, id: UnlockableId): boolean {
  if (id.kind === 'hull') return profile.unlocked.hulls.includes(id.id);
  if (id.kind === 'sector') return profile.unlocked.sectors.includes(id.id);
  return profile.unlocked.modes.includes(id.id);
}

/** Everything newly satisfied that is not already unlocked. Idempotent — never revokes (R17). */
export function evaluateUnlocks(profile: Profile): UnlockableId[] {
  const granted: UnlockableId[] = [];
  for (const u of UNLOCKS) {
    if (isUnlocked(profile, u.id)) continue;
    if (ruleSatisfied(u.rule, profile)) granted.push(u.id);
  }
  return granted;
}

export function unlockDescription(id: UnlockableId): string {
  const u = UNLOCKS.find((x) => idKey(x.id) === idKey(id));
  return u?.description ?? '';
}
